import { useEffect, useRef } from "react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { useToast } from "@/components/ui/UseToast";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";
import { useSelector } from "react-redux";
import { customerLogin } from "@/features/auth/AuthSlice";
import { IMAGES } from "@/assets/IMAGES";
import { ItsData } from "../UserLogin";

interface GoogleCredentialResponse {
  credential: string;
}

declare global {
  interface Window {
    google: any;
  }
}

const { VITE_ORG_ID, VITE_IS_PRODUCTION, VITE_IS_LOCAL, VITE_LOCAL_URL } =
  import.meta.env;

interface GoogleLoginProps {
  setUsers: (users: ItsData[]) => void; // Ensure correct typing
}
const GoogleLogin = ({ setUsers }: GoogleLoginProps) => {
  const { toast } = useToast();
  const navigate = useNavigate(); // Initialize the navigation hook
  const dispatch = useDispatch<AppDispatch>();
  const userPersona = localStorage.getItem("userType");

  const { loading, error, isEmailVerify } = useSelector(
    (state: RootState) => state.auth
  );

  const isAuthenticated = Boolean(localStorage.getItem("userToken"));
  const location = useLocation();

  const buttonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const loadGoogleScript = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });
        if (buttonRef.current) {
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: "outline",
            size: "large",
          });
        }
      }
    };

    if (!window.google) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = loadGoogleScript;
      document.body.appendChild(script);
    } else {
      loadGoogleScript();
    }
  }, []);

  const onSubmit = async ({ email }: { email: string }) => {
    const lowerCaseEmail = email.toLowerCase(); // Convert email to lowercase

    dispatch(
      customerLogin({
        identifier: lowerCaseEmail,
        org_id: VITE_ORG_ID,
        rememberme: false,
        domain:
          VITE_IS_LOCAL === "true"
            ? VITE_LOCAL_URL
            : VITE_IS_PRODUCTION === "true"
              ? window.location.hostname
              : window.location.host,
      })
    )
      .unwrap()
      .then((response) => {
        if (response.users?.length > 1) {
          setUsers(response.users);
        } else {
          toast({
            variant: "success",
            description: (
              <div className="flex items-center space-x-2 w-full h-10">
                <img
                  src={IMAGES.Status_icon}
                  alt="Success"
                  className="w-6 h-6"
                />
                <span>{response.message}</span>
              </div>
            ),
          });
          navigate("/user-login-otp", {
            state: { lowerCaseEmail, emailResponse: response },
          });
        }
      })
      .catch((error) => {
        console.error("Error Message", error === "User not Regisered.");
        if (error === "User not Regisered.") {
          toast({
            variant: "destructive",
            description: "User not Registered.",
          });
          navigate("/user-register", {
            state: { lowerCaseEmail, message: "User not Registered." },
          });
        } else {
          toast({
            variant: "destructive",
            description: error || "An error occurred.",
          });
        }
      });
  };

  const handleCredentialResponse = (response: GoogleCredentialResponse) => {
    const decoded = jwtDecode<{ email: string } & JwtPayload>(
      response.credential
    );
    if (decoded?.email) {

      // Call onSubmit with the email
      onSubmit({ email: decoded.email });
    }
  };

  return <div ref={buttonRef}></div>;
};

export default GoogleLogin;
